import React from 'react';
import { useTheme } from '../../context/ThemeContext';

export type ScaleBulkLogoVariant = 'horizontal' | 'stacked' | 'symbol';
export type ScaleBulkLogoTheme = 'auto' | 'light' | 'dark';

export interface ScaleBulkLogoProps {
  /** Size preset or exact pixel height */
  size?: 'sm' | 'md' | 'lg' | 'xl' | number;
  /** Layout presentation: 'horizontal' (headers/sidebar), 'stacked' (auth/hero), or 'symbol' (icon-only) */
  variant?: ScaleBulkLogoVariant;
  /** Whether to show the wordmark (if false, falls back to symbol) */
  showWordmark?: boolean;
  /** Whether to show the 'SOCIAL MEDIA MANAGEMENT' descriptor/tagline */
  showTagline?: boolean;
  /** Force specific theme or auto-detect based on [data-theme="light"] */
  theme?: ScaleBulkLogoTheme;
  /** Additional CSS class names */
  className?: string;
  /** Optional click handler */
  onClick?: () => void;
  /** Inline style overrides */
  style?: React.CSSProperties;
  /** Priority loading flag */
  priority?: boolean;
}

export const ScaleBulkLogo: React.FC<ScaleBulkLogoProps> = ({
  size = 'md',
  variant = 'horizontal',
  showWordmark = true,
  showTagline = false,
  theme = 'auto',
  className = '',
  onClick,
  style,
  priority = false
}) => {
  let activeTheme: 'dark' | 'light' = 'dark';
  try {
    const themeCtx = useTheme();
    if (themeCtx?.theme) activeTheme = themeCtx.theme;
  } catch {
    if (typeof document !== 'undefined') {
      activeTheme = document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark';
    }
  }

  const effectiveTheme: 'dark' | 'light' = theme === 'auto' ? activeTheme : theme;
  // Determine pixel height
  const pixelHeight: number = typeof size === 'number'
    ? size
    : size === 'sm'
    ? 34
    : size === 'lg'
    ? 44
    : size === 'xl'
    ? 56
    : 40; // 'md' default

  // Determine effective variant
  const effectiveVariant: ScaleBulkLogoVariant = !showWordmark ? 'symbol' : variant;

  // Resolve source paths and aspect ratios for dark and light variants
  let darkSrc = '/brand/scalebulk-logo-dark.png';
  let lightSrc = '/brand/scalebulk-logo-light.png';
  let aspectRatio = '901 / 240';
  let baseWidth = Math.round(pixelHeight * (901 / 240));

  if (effectiveVariant === 'symbol') {
    darkSrc = '/brand/scalebulk-symbol-dark.png';
    lightSrc = '/brand/scalebulk-symbol-light.png';
    aspectRatio = '1 / 1';
    baseWidth = pixelHeight;
  } else if (effectiveVariant === 'stacked') {
    if (showTagline) {
      darkSrc = '/brand/scalebulk-logo-stacked-dark.png';
      lightSrc = '/brand/scalebulk-logo-stacked-light.png';
      aspectRatio = '908 / 842';
      baseWidth = Math.round(pixelHeight * (908 / 842));
    } else {
      darkSrc = '/brand/scalebulk-logo-stacked-compact-dark.png';
      lightSrc = '/brand/scalebulk-logo-stacked-compact-light.png';
      aspectRatio = '908 / 795';
      baseWidth = Math.round(pixelHeight * (908 / 795));
    }
  } else {
    // horizontal
    if (showTagline) {
      darkSrc = '/brand/scalebulk-logo-horizontal-with-tagline-dark.png';
      lightSrc = '/brand/scalebulk-logo-horizontal-with-tagline-light.png';
      aspectRatio = '822 / 240';
      baseWidth = Math.round(pixelHeight * (822 / 240));
    } else {
      darkSrc = '/brand/scalebulk-logo-horizontal-dark.png';
      lightSrc = '/brand/scalebulk-logo-horizontal-light.png';
      aspectRatio = '901 / 240';
      baseWidth = Math.round(pixelHeight * (901 / 240));
    }
  }

  // WebP sources for modern browsers
  let darkWebp = darkSrc.replace('.png', '.webp');
  let lightWebp = lightSrc.replace('.png', '.webp');

  // For horizontal header logo, use specifically optimized high-DPI header WebP and PNG
  if (effectiveVariant === 'horizontal' && !showTagline) {
    darkWebp = '/brand/scalebulk-logo-horizontal-dark-header.webp';
    lightWebp = '/brand/scalebulk-logo-horizontal-light-header.webp';
    darkSrc = '/brand/scalebulk-logo-horizontal-dark-header.png';
    lightSrc = '/brand/scalebulk-logo-horizontal-light-header.png';
  }

  const altText = showTagline
    ? 'ScaleBulk — Social Media Management'
    : 'ScaleBulk';

  const containerStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: onClick ? 'pointer' : 'default',
    userSelect: 'none',
    textDecoration: 'none',
    flexShrink: 0,
    height: `${pixelHeight}px`,
    width: 'auto',
    maxWidth: '100%',
    lineHeight: 0,
    ...style
  };

  const imageBaseStyle: React.CSSProperties = {
    height: `${pixelHeight}px`,
    width: 'auto',
    maxWidth: '100%',
    objectFit: 'contain',
    aspectRatio,
    display: 'block',
    pointerEvents: 'none'
  };

  return (
    <div
      className={`scale-bulk-brand-logo ${className}`}
      onClick={onClick}
      style={containerStyle}
      role={onClick ? 'button' : undefined}
      aria-label={onClick ? altText : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); } : undefined}
    >
      {/* 
        Single theme-aware logo picture: prevents downloading both variants eagerly
      */}
      {effectiveTheme === 'dark' ? (
        <picture className="scalebulk-logo-dark scalebulk-logo-force-visible" style={{ display: 'contents' }}>
          <source type="image/webp" srcSet={darkWebp} />
          <img
            src={darkSrc}
            alt={altText}
            width={baseWidth}
            height={pixelHeight}
            className="scalebulk-logo-img"
            style={imageBaseStyle}
            loading={priority ? 'eager' : 'lazy'}
            decoding={priority ? 'sync' : 'async'}
            {...(priority ? ({ fetchPriority: 'high' } as any) : {})}
          />
        </picture>
      ) : (
        <picture className="scalebulk-logo-light scalebulk-logo-force-visible" style={{ display: 'contents' }}>
          <source type="image/webp" srcSet={lightWebp} />
          <img
            src={lightSrc}
            alt={altText}
            width={baseWidth}
            height={pixelHeight}
            className="scalebulk-logo-img"
            style={imageBaseStyle}
            loading={priority ? 'eager' : 'lazy'}
            decoding={priority ? 'sync' : 'async'}
            {...(priority ? ({ fetchPriority: 'high' } as any) : {})}
          />
        </picture>
      )}
    </div>
  );
};
